/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {KTSVG} from '../../../../_fds/helpers'
import {CardTeamMember} from './CardTeamMember'
import {BaseEmployeeModel} from '../core/_models'
import {
  convertBaseEmployeesToResources,
  getPositionColor,
  getPositionDisplayName,
  toTitleCase,
} from '../helpers/generals'
import {useStore} from '../store'
import {FdsSelect} from './common/FdsSelect'

export function TeamMembers() {
  const [filteredEmployees, setFilteredEmployees] = useState<Array<BaseEmployeeModel>>([])
  // Employees are fetched with include_inactive, so leavers are in the store —
  // this page hides them unless asked for. Local state on purpose: the store's
  // searchString/selectedPosition are shared with the schedule board, and this
  // filter only makes sense for the card grid.
  const [showInactive, setShowInactive] = useState(false)

  const {
    handleState,
    employeeHours,
    availablePositions,
    selectedPosition,
    employees,
    searchString,
  } = useStore()

  useEffect(() => {
    const filteredByPositionEmployees = employees.filter((employee: BaseEmployeeModel) => {
      if (!showInactive && employee.is_active === false) {
        return false
      }
      if (selectedPosition === 'all') {
        return true
      }
      return employee.position === selectedPosition
    })

    if (searchString === '') {
      // console.log('filteredByPositionEmployees', filteredByPositionEmployees)
      setFilteredEmployees(filteredByPositionEmployees)
      handleState(convertBaseEmployeesToResources(filteredByPositionEmployees) || [], 'resources')
      return
    }
    const filteredEmployees = filteredByPositionEmployees.filter((employee: BaseEmployeeModel) => {
      const fullName = `${employee.first_name.toLowerCase()} ${employee.last_name.toLowerCase()}`
      return fullName.toLowerCase().includes(searchString.toLowerCase())
    })
    setFilteredEmployees(filteredEmployees)
    handleState(convertBaseEmployeesToResources(filteredEmployees) || [], 'resources')
  }, [searchString, employees, selectedPosition, showInactive])

  return (
    <>
      <div className='d-flex flex-wrap flex-stack mb-6'>
        <h3 className='fw-semibold my-2'>
          Team Members
          <span className='fs-6 text-gray-400 fw-bold ms-1'>
            (
            {filteredEmployees.length === employees.length
              ? employees.length
              : `${filteredEmployees.length}/${employees.length}`}
            )
          </span>
        </h3>

        <div className='d-flex my-2'>
          <div className='d-flex align-items-center position-relative me-4'>
            <KTSVG
              path='/media/icons/duotune/general/gen021.svg'
              className='svg-icon-3 position-absolute ms-3'
            />
            <input
              type='text'
              id='fds_filter_search'
              className='form-control form-control-white form-control-sm w-150px ps-9'
              placeholder='Search member'
              value={searchString}
              onChange={(e) => handleState(e.target.value, 'searchString')}
            />
          </div>

          <div className='form-check form-check-custom form-check-solid form-check-sm me-4'>
            <input
              className='form-check-input'
              type='checkbox'
              id='fds_filter_show_inactive'
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <label
              className='form-check-label text-gray-600 fw-semibold'
              htmlFor='fds_filter_show_inactive'
            >
              Show inactive
            </label>
          </div>

          <FdsSelect
            ariaLabel='Position'
            value={selectedPosition}
            onChange={(position) => handleState(position, 'selectedPosition')}
            options={[
              {value: 'all', label: 'All'},
              ...availablePositions
                .filter((pos) => pos.position && pos.position !== 'all') // 'all' is the store's placeholder
                .map((pos) => ({
                  value: pos.position as string,
                  label: pos.name || toTitleCase(pos.position),
                })),
            ]}
          />
        </div>
      </div>

      <div className='row g-6 g-xl-9'>
        {filteredEmployees.map((employee) => (
          <div className='col-md-6 col-xxl-4' key={employee.id}>
            <CardTeamMember
              key={employee.id}
              employee_id={employee.id}
              default_position={employee.position}
              color={getPositionColor(employee.position)}
              firstname={employee.first_name}
              lastname={employee.last_name}
              hide_in_schedule={employee.hide_in_schedule}
              hours={employeeHours.get(employee.id) || 0}
              job={getPositionDisplayName(employee.position)}
              pay_rate={employee.pay_amount}
              worker_category={employee.worker_category_label}
            />
          </div>
        ))}
      </div>
    </>
  )
}
